/**
 * Export BODS (TransXChange) Modal
 *
 * Mirrors ExportGtfsModal but emits a UK BODS-publishable TransXChange dataset.
 * Adds the operator identity BODS requires (National Operator Code, operator
 * name, optional licence). Route selection + agency/feed metadata + the fallback
 * calendar are shared with the GTFS export (they feed the same `buildGtfsFeed`
 * intermediate). The output is manual-publish: download the .zip, then upload it
 * to your BODS operator account.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@liftango/ui-icons';

import { useFixedRoutes } from '../../context/FixedRoutesContext';
import {
  buildBodsTransXChange,
  writeBodsZip,
  type BodsExportOptions,
} from '../../utils/fixedRoutes/bodsExporter';
import { downloadBlob } from '../../utils/fixedRoutes/exportHelpers';
import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';
import { DEFAULT_SIMULATION_DATE } from '../../config/constants';
import { formatDateAsWallTime } from '../../utils/dateTimeUtils';
import type { ServiceDaysMask } from '../../types/fixedRoutes';

interface ExportBodsModalProps {
  open: boolean;
  onClose: () => void;
}

const DAY_FIELDS: Array<{ key: keyof ServiceDaysMask; label: string }> = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' },
];

const WEEKDAY_MASK: ServiceDaysMask = {
  monday: true, tuesday: true, wednesday: true, thursday: true, friday: true,
  saturday: false, sunday: false,
};

const wallTimeStamp = (): string => formatDateAsWallTime(new Date());

const plusOneYear = (isoDate: string): string => {
  const [y, m, d] = isoDate.split('-');
  return `${String(Number(y) + 1).padStart(4, '0')}-${m}-${d}`;
};

const slugify = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'feed';

export function ExportBodsModal({ open, onClose }: ExportBodsModalProps) {
  const { t } = useTranslation('fixedRoutes');
  const { stops, routes, trafficMultiplier } = useFixedRoutes();

  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London', []);

  const [selectedRouteIds, setSelectedRouteIds] = useState<Set<string>>(
    () => new Set(routes.map((r) => r.id)),
  );
  const [feedName, setFeedName] = useState('Atlas');
  const [agencyName, setAgencyName] = useState('Atlas');
  const [agencyUrl, setAgencyUrl] = useState('https://liftango.com');
  const [agencyLang, setAgencyLang] = useState('en');
  // BODS operator identity.
  const [noc, setNoc] = useState('');
  const [operatorName, setOperatorName] = useState('');
  const [licenceNumber, setLicenceNumber] = useState('');
  const [days, setDays] = useState<ServiceDaysMask>(WEEKDAY_MASK);
  const [startDate, setStartDate] = useState(DEFAULT_SIMULATION_DATE);
  const [endDate, setEndDate] = useState(() => plusOneYear(DEFAULT_SIMULATION_DATE));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options: BodsExportOptions = useMemo(
    () => ({
      selectedRouteIds: Array.from(selectedRouteIds),
      defaultServiceDays: days,
      defaultServiceDateRange: { startDate, endDate },
      meta: {
        feedName,
        agencyName,
        agencyUrl,
        agencyTimezone: timezone,
        agencyLang,
        feedVersion: wallTimeStamp().slice(0, 19),
      },
      operator: {
        nationalOperatorCode: noc,
        operatorName: operatorName || agencyName,
        licenceNumber: licenceNumber || undefined,
      },
      // Wall-clock creation stamp for the TransXChange header (xsd:dateTime).
      creationDateTime: new Date().toISOString().slice(0, 19),
    }),
    [selectedRouteIds, days, startDate, endDate, feedName, agencyName, agencyUrl, agencyLang, timezone, noc, operatorName, licenceNumber],
  );

  const preview = useMemo(
    () => buildBodsTransXChange({ stops, routes, trafficMultiplier }, options),
    [stops, routes, trafficMultiplier, options],
  );

  const toggleRoute = (id: string) => {
    setSelectedRouteIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = selectedRouteIds.size === routes.length && routes.length > 0;
  const toggleAll = () =>
    setSelectedRouteIds(allSelected ? new Set() : new Set(routes.map((r) => r.id)));

  const setDay = (key: keyof ServiceDaysMask, value: boolean) =>
    setDays((prev) => ({ ...prev, [key]: value }));

  const canDownload =
    selectedRouteIds.size > 0 && preview.counts.services > 0 && noc.trim().length > 0 && !busy;

  const handleDownload = async () => {
    const start = Date.now();
    setBusy(true);
    setError(null);
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_start, {
      export_type: 'bods',
      export_source: 'fixed_routes',
      item_count: preview.counts.services,
      timestamp: new Date().toISOString(),
    });
    try {
      const base = `bods-${slugify(feedName)}-${wallTimeStamp().slice(0, 10)}`;
      const blob = await writeBodsZip(preview, base);
      downloadBlob(blob, `${base}.zip`);
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_complete, {
        export_type: 'bods',
        export_source: 'fixed_routes',
        item_count: preview.counts.services,
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_fail, {
        feature_area: 'export',
        error_category: 'export_error',
        error_code: err instanceof Error ? err.name : 'unknown_error',
        retryable: true,
        export_type: 'bods',
        export_source: 'fixed_routes',
        item_count: preview.counts.services,
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon name="bus" size={20} />
          {t('bodsExport.title', 'Export to BODS (TransXChange)')}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'bodsExport.description',
              'Generate a UK BODS-publishable TransXChange dataset from your fixed routes. Download the .zip, then upload it to your BODS operator account.',
            )}
          </Typography>

          {/* Operator identity */}
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="flag" size={18} />
              <Typography variant="subtitle2">{t('bodsExport.operator', 'Operator (BODS)')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('bodsExport.noc', 'National Operator Code (NOC)')}
                value={noc}
                onChange={(e) => setNoc(e.target.value)}
                size="small"
                required
                error={noc.trim().length === 0}
                helperText={noc.trim().length === 0 ? t('bodsExport.nocRequired', 'Required by BODS') : undefined}
                sx={{ width: 220 }}
              />
              <TextField
                label={t('bodsExport.operatorName', 'Operator name')}
                value={operatorName}
                onChange={(e) => setOperatorName(e.target.value)}
                size="small"
                fullWidth
              />
            </Box>
            <TextField
              label={t('bodsExport.licence', 'Operator licence number (optional)')}
              value={licenceNumber}
              onChange={(e) => setLicenceNumber(e.target.value)}
              size="small"
              fullWidth
            />
          </Stack>

          <Divider />

          {/* Route selection */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon name="route" size={18} />
                <Typography variant="subtitle2">{t('bodsExport.routes', 'Routes to include')}</Typography>
              </Box>
              <Button size="small" onClick={toggleAll} disabled={routes.length === 0}>
                {allSelected ? t('bodsExport.selectNone', 'Select none') : t('bodsExport.selectAll', 'Select all')}
              </Button>
            </Box>
            <List dense sx={{ maxHeight: 160, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {routes.map((route) => (
                <ListItem key={route.id} disablePadding>
                  <ListItemButton onClick={() => toggleRoute(route.id)} dense>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox edge="start" checked={selectedRouteIds.has(route.id)} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                    <ListItemText primary={route.name} secondary={route.shortName} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          <Divider />

          {/* Agency / feed metadata */}
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="flag" size={18} />
              <Typography variant="subtitle2">{t('bodsExport.agency', 'Feed metadata')}</Typography>
            </Box>
            <TextField
              label={t('bodsExport.feedName', 'Feed name')}
              value={feedName}
              onChange={(e) => setFeedName(e.target.value)}
              size="small"
              fullWidth
            />
          </Stack>

          <Divider />

          {/* Fallback calendar */}
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="clock" size={18} />
              <Typography variant="subtitle2">{t('bodsExport.fallbackCalendar', 'Fallback service calendar')}</Typography>
            </Box>
            <FormGroup row>
              {DAY_FIELDS.map(({ key, label }) => (
                <FormControlLabel
                  key={key}
                  control={<Checkbox size="small" checked={days[key]} onChange={(e) => setDay(key, e.target.checked)} />}
                  label={label}
                />
              ))}
            </FormGroup>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="date"
                label={t('bodsExport.startDate', 'Start date')}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label={t('bodsExport.endDate', 'End date')}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Stack>

          <Divider />

          {/* Summary + warnings */}
          <Alert severity="info" icon={<Icon name="circle-info" size={20} />}>
            <AlertTitle>{t('bodsExport.summaryTitle', 'Export summary')}</AlertTitle>
            {t('bodsExport.summary', {
              defaultValue: '{{services}} services · {{lines}} lines · {{vehicleJourneys}} journeys · {{stops}} stops',
              services: preview.counts.services,
              lines: preview.counts.lines,
              vehicleJourneys: preview.counts.vehicleJourneys,
              stops: preview.counts.stopPoints,
            })}
          </Alert>

          {preview.counts.stopsWithoutAtco > 0 && (
            <Alert severity="warning" icon={<Icon name="triangle-exclamation" size={20} />}>
              <AlertTitle>
                {t('bodsExport.atcoWarnTitle', {
                  defaultValue: '{{count}} stop(s) not NaPTAN-matched',
                  count: preview.counts.stopsWithoutAtco,
                })}
              </AlertTitle>
              {t(
                'bodsExport.atcoWarnBody',
                'Stops without a NaPTAN ATCO code are exported with a synthetic reference. The file is valid but not registration-grade until those stops are matched to NaPTAN.',
              )}
            </Alert>
          )}

          {preview.skippedRoutes.length > 0 && (
            <Alert severity="warning" icon={<Icon name="triangle-exclamation" size={20} />}>
              <AlertTitle>
                {t('bodsExport.skippedTitle', { defaultValue: '{{count}} route(s) skipped', count: preview.skippedRoutes.length })}
              </AlertTitle>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {preview.skippedRoutes.map((s) => (
                  <li key={s.routeId}>{s.routeName}: {s.reason}</li>
                ))}
              </Box>
            </Alert>
          )}

          {error && (
            <Alert severity="error" icon={<Icon name="triangle-exclamation" size={20} />}>
              <AlertTitle>{t('bodsExport.errorTitle', 'Export failed')}</AlertTitle>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={<Icon name="file-arrow-down" size={20} />}
          onClick={handleDownload}
          disabled={!canDownload}
        >
          {t('bodsExport.download', 'Download TransXChange')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
